'use client';
import React, { useState, useEffect, useCallback } from 'react';
import AppLayout from '@/components/AppLayout';
import AppImage from '@/components/ui/AppImage';
import PriceBadge from '@/components/ui/PriceBadge';
import { PRODUCTS, type AlertRule } from '@/data/marketData';
import { Heart, Bell, BellOff, Trash2, Plus, Edit3, X, Sun, Moon, Smartphone, Mail, ShieldCheck, Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import Link from 'next/link';
import { useAuth } from '@/contexts/AuthContext';
import {
  subscribeToPush,
  unsubscribeFromPush,
  isPushSubscribed,
  getPushPermissionState,
  updateBulletinPreferences,
} from '@/lib/pushNotifications';
import {
  getUserAlerts,
  createAlert,
  updateAlert,
  deleteAlert,
  type PriceAlert,
} from '@/lib/priceAlertService';
import {
  subscribeToProductPrices,
  type PriceAlertMatch,
} from '@/lib/realtimePriceService';

interface AlertForm {
  productId: string;
  condition: 'below' | 'above';
  threshold: number;
}

export default function WatchlistAlertSettingsPage() {
  const { user } = useAuth();
  const [lang, setLang] = useState<'en' | 'ta'>('en');
  const [watchedIds] = useState<Set<string>>(
    new Set(PRODUCTS.filter(p => p.isWatched).map(p => p.id))
  );
  const [alertRules, setAlertRules] = useState<PriceAlert[]>([]);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [showAddAlert, setShowAddAlert] = useState(false);
  const [editingAlert, setEditingAlert] = useState<PriceAlert | null>(null);
  const [realtimeConnected, setRealtimeConnected] = useState(false);

  // Push notification state
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushPermission, setPushPermission] = useState<'granted' | 'denied' | 'default' | 'unsupported'>('default');
  const [pushLoading, setPushLoading] = useState(false);
  const [morningBulletin, setMorningBulletin] = useState(true);
  const [eveningBulletin, setEveningBulletin] = useState(false);
  const [bulletinSaving, setBulletinSaving] = useState(false);

  const watchedProducts = PRODUCTS.filter(p => watchedIds.has(p.id));

  const { register, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm<AlertForm>({
    defaultValues: { condition: 'below', threshold: 50 }
  });
  const conditionValue = watch('condition');

  // Load alerts and push state on mount
  useEffect(() => {
    if (!user) return;
    setAlertsLoading(true);
    getUserAlerts(user.id).then(alerts => {
      setAlertRules(alerts);
      setAlertsLoading(false);
    });

    isPushSubscribed().then(setPushSubscribed);
    getPushPermissionState().then(setPushPermission);
  }, [user]);

  // Real-time price subscription — triggers alerts instantly on price change
  useEffect(() => {
    if (!user) return;

    const unsubscribe = subscribeToProductPrices(
      user.id,
      (_updatedPrice) => {
        // Price updated in DB — mark real-time as connected
        setRealtimeConnected(true);
      },
      (match: PriceAlertMatch) => {
        // Threshold crossed — show instant in-app toast notification
        const conditionLabel = match.condition === 'below'
          ? (lang === 'en' ? 'dropped below' : 'கீழே சென்றது')
          : (lang === 'en' ? 'rose above' : 'மேலே சென்றது');

        const productLabel = lang === 'en' ? match.productName : match.productNameTa;

        toast.success(
          lang === 'en'
            ? `🚨 ${productLabel} ${conditionLabel} ₹${match.threshold} — now ₹${match.currentPrice}/kg`
            : `🚨 ${productLabel} ₹${match.threshold} ${conditionLabel} — இப்போது ₹${match.currentPrice}/kg`,
          { duration: 8000, id: `alert-${match.alertId}` }
        );

        // Update last_triggered_at in local state so UI reflects it
        setAlertRules(prev =>
          prev.map(a =>
            a.id === match.alertId
              ? { ...a, lastTriggeredAt: new Date().toISOString() }
              : a
          )
        );
      }
    );

    setRealtimeConnected(true);

    return () => {
      unsubscribe();
      setRealtimeConnected(false);
    };
  }, [user, lang]);

  const handleTogglePush = useCallback(async () => {
    if (!user) {
      toast.error(lang === 'en' ? 'Please sign in to enable notifications' : 'அறிவிப்புகளை இயக்க உள்நுழையவும்');
      return;
    }
    setPushLoading(true);
    try {
      if (pushSubscribed) {
        await unsubscribeFromPush(user.id);
        setPushSubscribed(false);
        toast(lang === 'en' ? 'Push notifications disabled' : 'புஷ் அறிவிப்புகள் முடக்கப்பட்டன', { icon: '🔕' });
      } else {
        const sub = await subscribeToPush(user.id);
        if (sub) {
          setPushSubscribed(true);
          setPushPermission('granted');
          toast.success(lang === 'en' ? 'Push notifications enabled!' : 'புஷ் அறிவிப்புகள் இயக்கப்பட்டன!');
        } else {
          const perm = await getPushPermissionState();
          setPushPermission(perm);
          if (perm === 'denied') {
            toast.error(lang === 'en' ? 'Notifications blocked. Please allow in browser settings.' : 'அறிவிப்புகள் தடுக்கப்பட்டுள்ளன. உலாவி அமைப்புகளில் அனுமதிக்கவும்.');
          } else {
            toast.error(lang === 'en' ? 'Could not enable push notifications' : 'புஷ் அறிவிப்புகளை இயக்க முடியவில்லை');
          }
        }
      }
    } finally {
      setPushLoading(false);
    }
  }, [user, pushSubscribed, lang]);

  const handleBulletinToggle = useCallback(async (type: 'morning' | 'evening', value: boolean) => {
    if (type === 'morning') setMorningBulletin(value);
    else setEveningBulletin(value);

    if (!user || !pushSubscribed) return;
    setBulletinSaving(true);
    try {
      const newMorning = type === 'morning' ? value : morningBulletin;
      const newEvening = type === 'evening' ? value : eveningBulletin;
      await updateBulletinPreferences(user.id, newMorning, newEvening);
    } finally {
      setBulletinSaving(false);
    }
  }, [user, pushSubscribed, morningBulletin, eveningBulletin]);

  const toggleAlertActive = useCallback(async (alertId: string, currentState: boolean) => {
    setAlertRules(prev => prev.map(a => a.id === alertId ? { ...a, isActive: !currentState } : a));
    await updateAlert(alertId, { isActive: !currentState });
  }, []);

  const handleDeleteAlert = useCallback(async (alertId: string) => {
    setAlertRules(prev => prev.filter(a => a.id !== alertId));
    await deleteAlert(alertId);
    toast(lang === 'en' ? 'Alert deleted' : 'எச்சரிக்கை நீக்கப்பட்டது', { icon: '🗑️' });
  }, [lang]);

  const onSaveAlert = useCallback(async (data: AlertForm) => {
    const product = PRODUCTS.find(p => p.id === data.productId);
    if (!product || !user) return;

    if (editingAlert) {
      setAlertRules(prev => prev.map(a => a.id === editingAlert.id ? {
        ...a, condition: data.condition, threshold: data.threshold
      } : a));
      await updateAlert(editingAlert.id, { condition: data.condition, threshold: data.threshold });
      toast.success(lang === 'en' ? 'Alert updated' : 'எச்சரிக்கை புதுப்பிக்கப்பட்டது');
    } else {
      const newAlert = await createAlert(user.id, {
        productId: data.productId,
        productName: product.name,
        productNameTa: product.nameTa,
        condition: data.condition,
        threshold: data.threshold,
        isActive: true,
      });
      if (newAlert) {
        setAlertRules(prev => [newAlert, ...prev]);
        toast.success(lang === 'en' ? `Alert set for ${product.name}` : `${product.nameTa} க்கு எச்சரிக்கை அமைக்கப்பட்டது`);
      }
    }
    setShowAddAlert(false);
    setEditingAlert(null);
    reset();
  }, [user, editingAlert, lang, reset]);

  const openEdit = useCallback((alert: PriceAlert) => {
    setEditingAlert(alert);
    setValue('productId', alert.productId);
    setValue('condition', alert.condition);
    setValue('threshold', alert.threshold);
    setShowAddAlert(true);
  }, [setValue]);

  function ToggleSwitch({ enabled, onChange, label, disabled }: { enabled: boolean; onChange: () => void; label: string; disabled?: boolean }) {
    return (
      <button
        onClick={onChange}
        disabled={disabled}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40 disabled:cursor-not-allowed ${enabled ? 'bg-primary' : 'bg-muted'}`}
        role="switch"
        aria-checked={enabled}
        aria-label={label}
      >
        <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${enabled ? 'translate-x-6' : 'translate-x-1'}`} />
      </button>
    );
  }

  return (
    <AppLayout activeRoute="/watchlist-alert-settings" lang={lang} onLangToggle={() => setLang(l => l === 'en' ? 'ta' : 'en')}>
      <div className="max-w-screen-2xl mx-auto px-4 md:px-6 xl:px-10 2xl:px-16 py-6 space-y-8">
        {/* Header */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <h1 className="text-2xl font-bold text-foreground">
              {lang === 'en' ? 'My Watchlist & Alerts' : 'என் கண்காணிப்பு & எச்சரிக்கைகள்'}
            </h1>
            {user && (
              <span className={`flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${realtimeConnected ? 'bg-green-light text-drop-green' : 'bg-muted text-muted-foreground'}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${realtimeConnected ? 'bg-drop-green animate-pulse' : 'bg-muted-foreground'}`} />
                {realtimeConnected
                  ? (lang === 'en' ? 'Live' : 'நேரடி')
                  : (lang === 'en' ? 'Connecting…' : 'இணைக்கிறது…')}
              </span>
            )}
          </div>
          <p className="text-sm text-muted-foreground">
            {lang === 'en'
              ? `Tracking ${watchedProducts.length} products · ${alertRules.filter(a => a.isActive).length} active alerts`
              : `${watchedProducts.length} பொருட்களை கண்காணிக்கிறீர்கள் · ${alertRules.filter(a => a.isActive).length} செயலில் உள்ள எச்சரிக்கைகள்`}
          </p>
        </div>

        {/* Watchlist Section */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Heart size={18} className="text-red-alert" fill="currentColor" />
              <h2 className="text-lg font-bold text-foreground">
                {lang === 'en' ? 'Watchlist' : 'கண்காணிப்பு பட்டியல்'}
              </h2>
              <span className="text-xs font-semibold bg-secondary text-primary rounded-full px-2 py-0.5">
                {watchedProducts.length}
              </span>
            </div>
            <Link href="/product-category-browse" className="text-sm text-primary font-semibold hover:underline flex items-center gap-1">
              <Plus size={14} />
              {lang === 'en' ? 'Add Products' : 'பொருட்கள் சேர்'}
            </Link>
          </div>

          {watchedProducts.length === 0 ? (
            <div className="card-base p-10 text-center">
              <Heart size={40} className="text-muted-foreground mx-auto mb-4" />
              <h3 className="font-bold text-foreground mb-2">
                {lang === 'en' ? 'No products in watchlist yet' : 'இன்னும் கண்காணிப்பில் பொருட்கள் இல்லை'}
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                {lang === 'en' ? 'Follow products to get daily price updates and trend alerts' : 'தினசரி விலை புதுப்பிப்புகளுக்கு பொருட்களை கண்காணிக்கவும்'}
              </p>
              <Link href="/product-category-browse" className="btn-primary inline-flex items-center gap-2">
                <Plus size={16} />
                {lang === 'en' ? 'Browse Products' : 'பொருட்களை உலாவு'}
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {watchedProducts.map(product => (
                <div key={`watch-${product.id}`} className="card-base p-4 flex items-center gap-4 group">
                  <Link href={`/product-detail?id=${product.id}`} className="flex items-center gap-4 flex-1 min-w-0">
                    <div className="w-14 h-14 rounded-2xl overflow-hidden bg-muted flex-shrink-0">
                      <AppImage src={product.image} alt={product.imageAlt} width={56} height={56} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm text-foreground truncate">
                        {lang === 'en' ? product.name : product.nameTa}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        {lang === 'en' ? product.nameTa : product.name}
                      </p>
                      <div className="flex items-center gap-2 mt-1.5">
                        <span className="price-value text-base">₹{product.currentPrice}</span>
                        <PriceBadge trend={product.trend} change={product.priceChange} changePct={product.priceChangePct} />
                      </div>
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Alert Rules Section */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Bell size={18} className="text-primary" />
              <h2 className="text-lg font-bold text-foreground">
                {lang === 'en' ? 'Price Alerts' : 'விலை எச்சரிக்கைகள்'}
              </h2>
              <span className="text-xs font-semibold bg-secondary text-primary rounded-full px-2 py-0.5">
                {alertRules.length}
              </span>
            </div>
            <button
              onClick={() => { setEditingAlert(null); reset(); setShowAddAlert(true); }}
              className="flex items-center gap-1.5 text-sm text-primary font-semibold hover:underline"
            >
              <Plus size={14} />
              {lang === 'en' ? 'Add Alert' : 'எச்சரிக்கை சேர்'}
            </button>
          </div>

          {alertsLoading ? (
            <div className="card-base p-10 flex items-center justify-center gap-3">
              <Loader2 size={20} className="animate-spin text-primary" />
              <span className="text-sm text-muted-foreground">{lang === 'en' ? 'Loading alerts…' : 'எச்சரிக்கைகள் ஏற்றுகிறது…'}</span>
            </div>
          ) : alertRules.length === 0 ? (
            <div className="card-base p-10 text-center">
              <Bell size={40} className="text-muted-foreground mx-auto mb-4" />
              <h3 className="font-bold text-foreground mb-2">
                {lang === 'en' ? 'No price alerts set' : 'விலை எச்சரிக்கைகள் இல்லை'}
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                {lang === 'en' ? 'Get notified when tomato drops below ₹20 or cashew below ₹800' : 'தக்காளி ₹20க்கு கீழே அல்லது முந்திரி ₹800க்கு கீழே சென்றால் தெரிவிக்கவும்'}
              </p>
              <button onClick={() => setShowAddAlert(true)} className="btn-primary inline-flex items-center gap-2">
                <Plus size={16} />
                {lang === 'en' ? 'Create First Alert' : 'முதல் எச்சரிக்கை உருவாக்கு'}
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {alertRules.map(alert => {
                const product = PRODUCTS.find(p => p.id === alert.productId);
                return (
                  <div key={`alert-${alert.id}`} className={`card-base p-4 flex items-center gap-4 ${!alert.isActive ? 'opacity-60' : ''}`}>
                    {product && (
                      <div className="w-12 h-12 rounded-2xl overflow-hidden bg-muted flex-shrink-0">
                        <AppImage src={product.image} alt={product.imageAlt} width={48} height={48} className="w-full h-full object-cover" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm text-foreground">
                        {lang === 'en' ? alert.productName : alert.productNameTa}
                      </p>
                      <p className="text-sm text-muted-foreground mt-0.5">
                        {lang === 'en'
                          ? `Notify when price goes ${alert.condition} ₹${alert.threshold}/kg`
                          : `விலை ₹${alert.threshold}/kg ${alert.condition === 'below' ? 'கீழே' : 'மேலே'} சென்றால் தெரிவி`}
                      </p>
                      {alert.lastTriggeredAt && (
                        <p className="text-xs text-accent font-semibold mt-1">
                          {lang === 'en' ? 'Last triggered:' : 'கடைசியாக:'} {new Date(alert.lastTriggeredAt).toLocaleDateString('en-IN')}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${alert.isActive ? 'bg-green-light text-drop-green' : 'bg-muted text-muted-foreground'}`}>
                        {alert.isActive ? (lang === 'en' ? 'Active' : 'செயலில்') : (lang === 'en' ? 'Paused' : 'இடைநிறுத்தம்')}
                      </span>
                      <button
                        onClick={() => toggleAlertActive(alert.id, alert.isActive)}
                        className="w-8 h-8 flex items-center justify-center rounded-xl text-muted-foreground hover:text-primary hover:bg-secondary transition-all"
                        aria-label={alert.isActive ? 'Pause alert' : 'Activate alert'}
                      >
                        {alert.isActive ? <BellOff size={15} /> : <Bell size={15} />}
                      </button>
                      <button
                        onClick={() => openEdit(alert)}
                        className="w-8 h-8 flex items-center justify-center rounded-xl text-muted-foreground hover:text-primary hover:bg-secondary transition-all"
                        aria-label="Edit alert"
                      >
                        <Edit3 size={15} />
                      </button>
                      <button
                        onClick={() => handleDeleteAlert(alert.id)}
                        className="w-8 h-8 flex items-center justify-center rounded-xl text-muted-foreground hover:text-rise-red hover:bg-red-light transition-all"
                        aria-label="Delete alert"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Notification Preferences */}
        <section>
          <div className="flex items-center gap-2 mb-4">
            <Bell size={18} className="text-primary" />
            <h2 className="text-lg font-bold text-foreground">
              {lang === 'en' ? 'Notification Preferences' : 'அறிவிப்பு விருப்பங்கள்'}
            </h2>
          </div>

          <div className="card-base divide-y divide-border">
            {/* Push Notification Enable Banner */}
            <div className="p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${pushSubscribed ? 'bg-green-light' : 'bg-secondary'}`}>
                    {pushLoading
                      ? <Loader2 size={16} className="animate-spin text-primary" />
                      : pushSubscribed
                        ? <ShieldCheck size={16} className="text-drop-green" />
                        : <Smartphone size={16} className="text-primary" />
                    }
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground">
                      {lang === 'en' ? 'Push Notifications' : 'புஷ் அறிவிப்புகள்'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {pushPermission === 'denied'
                        ? (lang === 'en' ? 'Blocked in browser — allow in site settings' : 'உலாவியில் தடுக்கப்பட்டுள்ளது')
                        : pushSubscribed
                          ? (lang === 'en' ? 'Active — receiving price alerts & bulletins' : 'செயலில் — விலை எச்சரிக்கைகள் பெறுகிறீர்கள்')
                          : (lang === 'en' ? 'Enable to receive price alerts on this device' : 'இந்த சாதனத்தில் விலை எச்சரிக்கைகள் பெற இயக்கவும்')}
                    </p>
                  </div>
                </div>
                <ToggleSwitch
                  enabled={pushSubscribed}
                  onChange={handleTogglePush}
                  label="Push notifications toggle"
                  disabled={pushLoading || pushPermission === 'unsupported'}
                />
              </div>
            </div>

            {/* Daily Bulletins */}
            <div className="p-5">
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm font-semibold text-foreground">
                  {lang === 'en' ? 'Daily Market Bulletins' : 'தினசரி சந்தை புல்லட்டின்'}
                </p>
                {bulletinSaving && <Loader2 size={14} className="animate-spin text-muted-foreground" />}
              </div>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-amber-light rounded-xl flex items-center justify-center">
                      <Sun size={16} className="text-accent" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground">
                        {lang === 'en' ? 'Morning Bulletin' : 'காலை புல்லட்டின்'}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {lang === 'en' ? "Daily at 6:30 AM — Today's prices & top movers" : 'தினமும் காலை 6:30 — இன்றைய விலைகள்'}
                      </p>
                    </div>
                  </div>
                  <ToggleSwitch
                    enabled={morningBulletin}
                    onChange={() => handleBulletinToggle('morning', !morningBulletin)}
                    label="Morning bulletin toggle"
                    disabled={!pushSubscribed}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-secondary rounded-xl flex items-center justify-center">
                      <Moon size={16} className="text-primary" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground">
                        {lang === 'en' ? 'Evening Bulletin' : 'மாலை புல்லட்டின்'}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {lang === 'en' ? "Daily at 6:00 PM — Tomorrow's market preview" : 'தினமும் மாலை 6:00 — நாளைய சந்தை முன்னோட்டம்'}
                      </p>
                    </div>
                  </div>
                  <ToggleSwitch
                    enabled={eveningBulletin}
                    onChange={() => handleBulletinToggle('evening', !eveningBulletin)}
                    label="Evening bulletin toggle"
                    disabled={!pushSubscribed}
                  />
                </div>

                {!pushSubscribed && (
                  <p className="text-xs text-muted-foreground italic">
                    {lang === 'en' ? '↑ Enable push notifications above to activate bulletins' : '↑ புல்லட்டின்களை இயக்க மேலே புஷ் அறிவிப்புகளை இயக்கவும்'}
                  </p>
                )}
              </div>
            </div>

            {/* Other Channels */}
            <div className="p-5">
              <p className="text-sm font-semibold text-foreground mb-4">
                {lang === 'en' ? 'Other Alert Channels' : 'மற்ற எச்சரிக்கை சேனல்கள்'}
              </p>
              <div className="space-y-4">
                {[
                  { icon: <Mail size={16} className="text-accent" />, bg: 'bg-amber-light', labelEn: 'Email Alerts', labelTa: 'மின்னஞ்சல் எச்சரிக்கைகள்', subEn: 'Daily digest to your inbox', subTa: 'உங்கள் இன்பாக்ஸுக்கு தினசரி சுருக்கம்', comingSoon: true },
                  { icon: <Smartphone size={16} className="text-drop-green" />, bg: 'bg-green-light', labelEn: 'SMS Alerts', labelTa: 'SMS எச்சரிக்கைகள்', subEn: 'Text messages for critical price moves', subTa: 'முக்கியமான விலை மாற்றங்களுக்கு SMS', comingSoon: true },
                ].map(channel => (
                  <div key={`channel-${channel.labelEn}`} className="flex items-center justify-between opacity-60">
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 ${channel.bg} rounded-xl flex items-center justify-center`}>
                        {channel.icon}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground flex items-center gap-2">
                          {lang === 'en' ? channel.labelEn : channel.labelTa}
                          <span className="text-xs font-normal bg-muted text-muted-foreground px-2 py-0.5 rounded-full">
                            {lang === 'en' ? 'Coming soon' : 'விரைவில்'}
                          </span>
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {lang === 'en' ? channel.subEn : channel.subTa}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Add/Edit Alert Modal */}
      {showAddAlert && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
          <div className="w-full max-w-md bg-card rounded-4xl shadow-float scale-in overflow-hidden">
            <div className="flex items-center justify-between px-6 py-5 border-b border-border">
              <p className="font-bold text-foreground">
                {editingAlert
                  ? (lang === 'en' ? 'Edit Alert' : 'எச்சரிக்கை திருத்து')
                  : (lang === 'en' ? 'New Price Alert' : 'புதிய விலை எச்சரிக்கை')}
              </p>
              <button
                onClick={() => { setShowAddAlert(false); setEditingAlert(null); reset(); }}
                className="w-8 h-8 flex items-center justify-center rounded-xl text-muted-foreground hover:text-foreground hover:bg-muted"
                aria-label="Close modal"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSubmit(onSaveAlert)} className="px-6 py-6 space-y-5">
              {!editingAlert && (
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-2">
                    {lang === 'en' ? 'Select Product' : 'பொருள் தேர்வு'}
                  </label>
                  <select
                    className="input-base"
                    {...register('productId', { required: 'Please select a product' })}
                  >
                    <option value="">-- {lang === 'en' ? 'Choose a product' : 'பொருளை தேர்வு செய்யவும்'} --</option>
                    {PRODUCTS.map(p => (
                      <option key={`opt-${p.id}`} value={p.id}>
                        {lang === 'en' ? p.name : p.nameTa} — ₹{p.currentPrice}/{p.unit}
                      </option>
                    ))}
                  </select>
                  {errors.productId && <p className="text-xs text-rise-red mt-1.5">{errors.productId.message}</p>}
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-foreground mb-3">
                  {lang === 'en' ? 'Condition' : 'நிபந்தனை'}
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { value: 'below', labelEn: '📉 Price drops below', labelTa: '📉 விலை கீழே' },
                    { value: 'above', labelEn: '📈 Price rises above', labelTa: '📈 விலை மேலே' },
                  ].map(opt => (
                    <label key={`modal-cond-${opt.value}`} className="cursor-pointer">
                      <input type="radio" value={opt.value} {...register('condition')} className="sr-only" />
                      <div className={`p-3 rounded-2xl border text-center text-sm font-semibold transition-all ${conditionValue === opt.value ? 'bg-primary text-primary-foreground border-primary' : 'bg-muted border-border'}`}>
                        {lang === 'en' ? opt.labelEn : opt.labelTa}
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-2">
                  {lang === 'en' ? 'Price Threshold (₹)' : 'விலை வரம்பு (₹)'}
                </label>
                <input
                  type="number"
                  className="input-base text-xl font-bold font-tabular"
                  {...register('threshold', {
                    required: 'Enter a price threshold',
                    min: { value: 1, message: 'Must be at least ₹1' }
                  })}
                />
                {errors.threshold && <p className="text-xs text-rise-red mt-1.5">{errors.threshold.message}</p>}
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => { setShowAddAlert(false); setEditingAlert(null); reset(); }}
                  className="flex-1 btn-ghost border border-border"
                >
                  {lang === 'en' ? 'Cancel' : 'ரத்து'}
                </button>
                <button type="submit" className="flex-1 btn-primary">
                  {editingAlert
                    ? (lang === 'en' ? 'Update Alert' : 'எச்சரிக்கை புதுப்பி')
                    : (lang === 'en' ? 'Save Alert' : 'எச்சரிக்கை சேமி')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AppLayout>
  );
}